import { attemptToFixJson } from '@backrs_ui/common-ui/src/molecules/AskLubavButtons/askLubavApi.util';
import { fetchWrapper as fetch } from '@backrs_ui/grow-ui/src/utils/fetch/fetchWrapper';
import { getBackrsToken } from '@backrs_ui/grow-ui/src/utils/tokenHelpers';

export interface StreamedMessage {
  query_id: string;
  response_id: string;
  response: string;
  suggested_prompts: string[];
}

const defaultMessage: Record<string, string | string[]> = {
  query_id: '',
  response_id: '',
  response: '',
  suggested_prompts: [],
};

const LUBAV_API_URL = process.env.REACT_APP_LUBAV_API_BASEURL;

export async function fetchStreamedLubavMessage(
  url: string,
  requestBody: Record<string, unknown>,
  onChunk: (partialResponse: StreamedMessage) => void,
): Promise<number | undefined> {
  if (!LUBAV_API_URL) return undefined;

  const eventSourceUrl = `${LUBAV_API_URL}${url}`;
  const accessToken = getBackrsToken();
  let responseId: number | undefined;

  const response = await fetch(eventSourceUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
    },
    body: JSON.stringify(requestBody),
  });

  const reader = response.body?.getReader();
  let accumulatedResponse = '';
  let currentResponseLength = 0;

  while (reader) {
    const { done, value } = await reader.read();

    if (done) break;

    accumulatedResponse += new TextDecoder()
      .decode(value)
      .split('\n')
      .map((line) => line.replace(/^data: /, ''))
      .join('');

    const fixedJson = attemptToFixJson(accumulatedResponse, defaultMessage);
    const chunk = fixedJson
      ? JSON.parse(fixedJson)
      : {
          ...defaultMessage,
          response: accumulatedResponse.match(/"response":"([^"]*)/)?.[1] || '',
        };

    if (chunk.response_id) {
      responseId = parseInt(chunk.response_id);
    }

    // process suggested prompts
    if (chunk.suggested_prompts?.length > 0) {
      onChunk({ ...chunk, response: chunk.response || '' });
    }

    // process response
    if (chunk.response.length > currentResponseLength && isValidMarkdown(chunk.response)) {
      currentResponseLength = chunk.response.length;
      onChunk(chunk);
    }
  }

  return responseId;
}

export const isValidMarkdown = (markdown: string): boolean => {
  const isEven = (count: number): boolean => count % 2 === 0;

  const hasBalancedFormatting = (text: string): boolean => {
    const boldCount = (text.match(/\*\*/g) || []).length;
    const italicCount = (text.match(/_/g) || []).length;

    return isEven(boldCount) && isEven(italicCount);
  };

  const hasValidLists = (text: string): boolean => {
    return text.split('\n').every((line) => {
      const trimmed = line.trim();
      const isBullet = /^[-*+]\s+.+/.test(trimmed);
      const isNumbered = /^\d+\.\s+.+/.test(trimmed);
      const startsLikeList = /^[-*+]|^\d+\./.test(trimmed);

      return startsLikeList ? isBullet || isNumbered : true;
    });
  };

  const hasValidHeaders = (text: string): boolean => {
    return text.split('\n').every((line) => {
      const trimmed = line.trim();
      const isHeader = /^#{1,6}(?!#)/.test(trimmed);
      const isValid = /^#{1,6} +\S/.test(trimmed);

      return isHeader ? isValid : true;
    });
  };

  const hasNoTrailingBackslash = (text: string): boolean => {
    return !/\\+\s*$/.test(text);
  };

  return (
    hasBalancedFormatting(markdown) &&
    hasValidLists(markdown) &&
    hasValidHeaders(markdown) &&
    hasNoTrailingBackslash(markdown)
  );
};
